
import { isNullOrUndefined } from "../../../utils/utils";
import { IEventListener, TranscriptionNotifyEvent } from "../../interfaces/IEventListener";
import { HostAppSettingName, HostAppSettings } from "../../office-js/HostAppSettings";

/**
 * BroadcastService can be used broadcast specific TranscriptionNotifyEvent across the IEventListener, which has
 * registered for broadcasted TranscriptionNotifyEvent.
 * */
export class BroadcastService {

  private static instance: BroadcastService;

  private constructor() { }

  static GetInstance() {
    if (this.instance == null) this.instance = new BroadcastService();

    return this.instance;
  }

  // eventListenerSet will keep registered event listeners.
  private eventListenerMap: Map<TranscriptionNotifyEvent, Set<IEventListener>> = new Map<TranscriptionNotifyEvent, Set<IEventListener>>();
  private eventListenerToEventMap: Map<IEventListener, Set<TranscriptionNotifyEvent>> = new Map<IEventListener, Set<TranscriptionNotifyEvent>>();

  static IsNotifyTranscriptionEventToHostAppEnabled(): boolean {
    return HostAppSettings.IsSettingTrue(HostAppSettingName.NotifyTranscriptionEventToHostAppEnabled);
  }

  BroadcastEvent(eventName: TranscriptionNotifyEvent, payload?: any) {
    const eventListenerSet: Set<IEventListener> | undefined = this.eventListenerMap.get(eventName);
    if (!isNullOrUndefined(eventListenerSet)) {
      eventListenerSet!.forEach(function (eventListener) {
        eventListener.onReceive(eventName, payload);
      });
    }
  }

  /**
   * Register IEventListener for Passed TranscriptionNotifyEvent.
   * @method RegisterEventListenerForEvent
   * @param eventListener : IEventListener
   * @param event : TranscriptionNotifyEvent
   * @returns true, if registration is successfull, else false.
   */
  RegisterEventListenerForEvent(eventListener: IEventListener, event: TranscriptionNotifyEvent): boolean {
    if (isNullOrUndefined(eventListener)) {
      throw new Error('IEventListner is null | undefined');
    }
    let eventListenerSet: Set<IEventListener> | undefined = this.eventListenerMap.get(event);

    if (isNullOrUndefined(eventListenerSet)) {
      eventListenerSet = new Set<IEventListener>();
      this.eventListenerMap.set(event, eventListenerSet);
    }

    if (eventListenerSet?.has(eventListener)) {
      return false;
    }

    eventListenerSet!.add(eventListener);

    /* create mapping from eventListener to Events, which will help improve time complexity
    during this.UnregisterEventListener callbackify.*/
    let eventSet: Set<TranscriptionNotifyEvent> | undefined = this.eventListenerToEventMap.get(eventListener);

    if (isNullOrUndefined(eventSet)) {
      eventSet = new Set<TranscriptionNotifyEvent>();
      this.eventListenerToEventMap.set(eventListener, eventSet);
    }
    eventSet!.add(event);
    return true;
  }

  /**
   * Register IEventListener for Passed Array of TranscriptionNotifyEvent.
   * @method RegisterEventListenerForEvents
   * @param eventListener : IEventListener
   * @param eventArr : TranscriptionNotifyEvent[]
   * @returns boolean[], with success/failure response for each TranscriptionNotifyEvent in passed Array.
   */
  RegisterEventListenerForEvents(eventListener: IEventListener, eventArr: TranscriptionNotifyEvent[]): boolean[] {
    if (isNullOrUndefined(eventListener) || isNullOrUndefined(eventArr) || eventArr.length <= 0) {
      throw new Error('IEventListner or TranscriptionNotifyEvent Array is not valid');
    }
    const responseTable: Array<boolean> = new Array<boolean>();
    for (let index = 0; index < eventArr.length; index++) {
      responseTable[index] = this.RegisterEventListenerForEvent(eventListener, eventArr[index]);
    }
    return responseTable;
  }

  /**
   * Unregister the IEventListener from BroadcastService.
   * @method UnregisterEventListener
   * @param eventListener : IEventListener
   * @returns true, if unregister is succesful else false.
   */
  UnregisterEventListener(eventListener: IEventListener): boolean {
    if (isNullOrUndefined(eventListener)) {
      throw new Error('IEventListner  is null or undefined');
    }
    const eventSet: Set<TranscriptionNotifyEvent> | undefined = this.eventListenerToEventMap.get(eventListener);

    if (isNullOrUndefined(eventSet)) {
      return false;
    }

    //delete entry of eventListener from eventListenerToEventMap
    this.eventListenerToEventMap.delete(eventListener);

    /*iterate through eventSet of eventListener and remove eventListener
    from eventListenerSet of each event.*/
    eventSet!.forEach(function (event) {
      const eventListenerSet = BroadcastService.instance.eventListenerMap.get(event);
      if (!isNullOrUndefined(eventListenerSet)) {
        eventListenerSet?.delete(eventListener);

        // if no listener is registered for specific event, remove entry from eventListenerMap
        if (eventListenerSet!.size <= 0) {
          BroadcastService.instance.eventListenerMap.delete(event);
        }
      }
    });
    return true;
  }
}

